import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { LOCALE_PREFIXES, getLocalePrefixFromI18n } from "@/i18n";

interface SEOHeadProps {
  title?: string;
  description?: string;
  /** Base path without locale prefix, e.g. "/inquiry" */
  canonical?: string;
  noindex?: boolean;
  type?: "website" | "article";
  image?: string;
}

const SITE_NAME = "CRAFTFINDUS";
const DEFAULT_DESCRIPTION =
  "CRAFTFINDUS: Kostenlos den passenden Handwerksbetrieb finden oder als Handwerker qualifizierte Projektanfragen erhalten.";
const BASE_URL = "https://www.craftfindus.com";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

const OG_LOCALE_MAP: Record<string, string> = {
  de: "de_DE",
  "de-AT": "de_AT",
  "de-CH": "de_CH",
  nl: "nl_NL",
  "nl-BE": "nl_BE",
  fr: "fr_FR",
  "fr-BE": "fr_BE",
  pl: "pl_PL",
  en: "en_GB",
  wa: "wa",
};

export default function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  noindex = false,
  type = "website",
  image = DEFAULT_IMAGE,
}: SEOHeadProps) {
  const { i18n } = useTranslation();
  const currentPrefix = getLocalePrefixFromI18n(i18n.language);
  const currentConfig = LOCALE_PREFIXES.find((p) => p.prefix === currentPrefix);

  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} | Handwerksbetrieb finden & Projektanfragen erhalten`;

  // Canonical URL includes locale prefix
  const url = canonical
    ? `${BASE_URL}/${currentPrefix}${canonical}`
    : undefined;

  const currentOgLocale = OG_LOCALE_MAP[i18n.language] || "de_DE";
  const geoRegion = currentConfig?.locale.split("-")[1] || "DE";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
      )}
      {url && <link rel="canonical" href={url} />}

      {/* hreflang for all locale variants */}
      {canonical &&
        LOCALE_PREFIXES.map((lp) => (
          <link
            key={lp.prefix}
            rel="alternate"
            hrefLang={lp.locale}
            href={`${BASE_URL}/${lp.prefix}${canonical}`}
          />
        ))}
      {canonical && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${BASE_URL}/de${canonical}`}
        />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={currentOgLocale} />
      {LOCALE_PREFIXES.filter((lp) => lp.i18nCode !== i18n.language).map(
        (lp) => {
          const alt = OG_LOCALE_MAP[lp.i18nCode];
          return alt ? (
            <meta
              key={lp.prefix}
              property="og:locale:alternate"
              content={alt}
            />
          ) : null;
        }
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO signals */}
      <meta name="author" content="CRAFTFINDUS" />
      <meta name="geo.region" content={geoRegion} />
    </Helmet>
  );
}
